/* eslint-disable max-len */
import { openedModelToStatePatch } from 'Pods/ThreeDStudio/Services/openedModelToStatePatch/openedModelToStatePatch'
import {
  DEFAULT_UI,
  VIEWER_SIDEBAR_TAB,
  MOBILE_PANEL,
  ACTION,
  DEFAULT_SCHEMA,
  DEFAULT_VIEWER,
  DEFAULT_REMESH_SETTINGS,
  DEFAULT_TEXTURE_SETTINGS,
  DEFAULT_MODEL_SETTINGS,
  DEFAULT_EXPORT,
} from 'Pods/ThreeDStudio/Store/constants'

import { threeDStudioStore } from '../index'
import { EXPORT_STATUS } from '../slices/export'

export const setUiState = (patch, schemaPatch) => {
  threeDStudioStore.setState((state) => ({
    ui: { ...state.ui, ...patch },
    ...(schemaPatch && { schema: { ...state.schema, ...schemaPatch }}),
  }))
}

/**
 * Sets the active viewer sidebar tab
 * @param {string | undefined} activeViewerSidebarTab - One of: "remesh", "texture", "segment", "animate", "render", "assist" or undefined
 */
export const setActiveViewerSidebarTab = (activeViewerSidebarTab) => {
  const { ui } = threeDStudioStore.getState()
  const { isViewerSidebarOpen, activeViewerSidebarTab: currentActiveViewerSidebarTab } = ui
  const enhancedAction = {
    [VIEWER_SIDEBAR_TAB.REMESH]: ACTION.MESH_SIMPLIFICATION,
    [VIEWER_SIDEBAR_TAB.TEXTURE]: ACTION.RETEXTURE,
    [VIEWER_SIDEBAR_TAB.SEGMENT]: ACTION.MESH_SEGMENTATION,
    [VIEWER_SIDEBAR_TAB.ANIMATE]: null,
    [VIEWER_SIDEBAR_TAB.RENDER]: null,
    [VIEWER_SIDEBAR_TAB.VARIATIONS]: null,
    [VIEWER_SIDEBAR_TAB.ASSIST]: null,
  }[activeViewerSidebarTab]

  if (!isViewerSidebarOpen) {
    setUiState(
      {
        activeViewerSidebarTab,
        isViewerSidebarOpen: true,
        isModelSettingsOpen: false,
      },
      { enhancedAction },
    )

    return
  }

  if (currentActiveViewerSidebarTab === activeViewerSidebarTab) {
    setUiState(
      { isViewerSidebarOpen: false, activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab },
      { enhancedAction: DEFAULT_SCHEMA.enhancedAction },
    )

    return
  }

  setUiState({ activeViewerSidebarTab }, { enhancedAction })
}

/**
 * Sets whether the model settings sidebar (left side) is open or collapsed.
 * @param {boolean} isModelSettingsOpen
 */
export const setIsModelSettingsOpen = (isModelSettingsOpen) => {
  if (isModelSettingsOpen) {
    setUiState({
      isModelSettingsOpen,
      isViewerSidebarOpen: false,
      activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab,
    })

    return
  }

  setUiState({ isModelSettingsOpen })
}

/**
 * Sets whether the left (library) sidebar is open or collapsed.
 * @param {boolean} isSidebarOpen
 */
export const setIsSidebarOpen = (isSidebarOpen) => {
  setUiState({ isSidebarOpen })
}

/**
 * Opens the viewer sidebar on the Variations tab. Idempotent: unlike
 * setActiveViewerSidebarTab it never toggles the panel closed when Variations is
 * already active, so it is safe to call from completion handlers.
 */
export const openVariationsTab = () => {
  setUiState({
    isViewerSidebarOpen: true,
    activeViewerSidebarTab: VIEWER_SIDEBAR_TAB.VARIATIONS,
    isModelSettingsOpen: false,
  })
}

/**
 * Sets whether the viewer sidebar is open or collapsed.
 * @param {boolean} isViewerSidebarOpen
 */
export const setIsViewerSidebarOpen = (isViewerSidebarOpen) => {
  if (isViewerSidebarOpen) {
    setUiState(
      {
        activeViewerSidebarTab: VIEWER_SIDEBAR_TAB.REMESH,
        isViewerSidebarOpen: true,
        isModelSettingsOpen: false,
      },
      { enhancedAction: ACTION.MESH_SIMPLIFICATION },
    )

    return
  }

  setUiState(
    { isViewerSidebarOpen: false, activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab },
    { enhancedAction: DEFAULT_SCHEMA.enhancedAction },
  )
}

/**
 * Selects the active work panel on small screens (< md), where the three
 * desktop panels are presented as tabs. Also syncs the desktop open flags so
 * the conditionally-rendered content of each tab is actually mounted.
 * @param {string} activeMobilePanel - One of MOBILE_PANEL values
 */
export const setActiveMobilePanel = (activeMobilePanel) => {
  if (activeMobilePanel === MOBILE_PANEL.GENERATE) {
    setUiState(
      {
        activeMobilePanel,
        isModelSettingsOpen: true,
        isViewerSidebarOpen: false,
        activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab,
      },
      { enhancedAction: DEFAULT_SCHEMA.enhancedAction },
    )

    return
  }

  if (activeMobilePanel === MOBILE_PANEL.ENHANCE) {
    const { activeViewerSidebarTab } = threeDStudioStore.getState().ui
    const nextTab = activeViewerSidebarTab ?? VIEWER_SIDEBAR_TAB.REMESH

    setUiState(
      {
        activeMobilePanel,
        isViewerSidebarOpen: true,
        isModelSettingsOpen: false,
        activeViewerSidebarTab: nextTab,
      },
      nextTab === VIEWER_SIDEBAR_TAB.REMESH ? { enhancedAction: ACTION.MESH_SIMPLIFICATION } : undefined,
    )

    return
  }

  // MOBILE_PANEL.VIEWER — the viewer always renders, so no flags to force.
  setUiState({ activeMobilePanel })
}

/**
 * Sets the active texture tab inside Texture viewer sidebar.
 * @param {string} activeTextureTab - One of: "generate", "resolution", "edit"
 */
export const setActiveTextureTab = (activeTextureTab) => {
  setUiState({ activeTextureTab })
}

/**
 * Sets the active action tab inside Texture generate panel.
 * @param {string} activeTextureActionTab - One of: "image", "text"
 */
export const setActiveTextureActionTab = (activeTextureActionTab) => {
  setUiState({ activeTextureActionTab })
}

/**
 * Sets the opened model (e.g. selected library item) and syncs modelSettings
 * and optionSettings from the run payload so the form reflects the opened run.
 * @param {Object} openedModel - Model object; use {} to clear
 * @param {Object} options
 * @param {boolean} options.keepVariationsTab - Keep the variations tab open
 * (set when the open originates from a variation card click)
 */
export const setOpenedModel = (openedModel, { keepVariationsTab = false } = {}) => {
  const patch = openedModelToStatePatch(openedModel)
  // On small screens, surfacing a model means the viewer tab should take over.
  const hasModel = !!openedModel && Object.keys(openedModel).length > 0
  threeDStudioStore.setState((state) => {
    // Switching variations from the variations tab keeps it open instead of
    // letting the per-model parsers close the sidebar or steal the tab.
    const preserveVariationsTab = keepVariationsTab && hasModel &&
      state.ui?.isViewerSidebarOpen &&
      state.ui?.activeViewerSidebarTab === VIEWER_SIDEBAR_TAB.VARIATIONS
    const exportState = state.export.status === EXPORT_STATUS.ERROR
      ? { ...DEFAULT_EXPORT, selectedFormat: state.export.selectedFormat }
      : state.export
    return {
      ...state,
      ui: {
        ...state.ui,
        ...patch.ui,
        openedModel: patch.openedModel,
        ...(hasModel && { activeMobilePanel: MOBILE_PANEL.VIEWER }),
        ...(preserveVariationsTab && {
          isViewerSidebarOpen: true,
          activeViewerSidebarTab: VIEWER_SIDEBAR_TAB.VARIATIONS,
          isModelSettingsOpen: false,
        }),
      },
      viewer: { ...DEFAULT_VIEWER, ...(patch.viewer || {}) },
      schema: { ...state.schema, ...patch.schema },
      modelSettings: {
        ...DEFAULT_MODEL_SETTINGS,
        ...(patch.modelSettings || {}),
      },
      remeshSettings: { ...DEFAULT_REMESH_SETTINGS, ...(patch.remeshSettings || {}) },
      textureSettings: { ...DEFAULT_TEXTURE_SETTINGS, ...(patch.textureSettings || {}) },
      // Pending exports survive model switches so the completion subscription
      // stays alive; stale export errors reset like the rest of the model UI.
      export: { ...exportState, ...(patch.export || {}) },
      generation: {
        ...state.generation,
        ...patch.generation,
        hasFailed: false,
        errorDetails: {},
        errorId: '',
      },
    }
  })
}

/**
 * Updates opened model title (prompt) in the store.
 * @param {string} prompt
 */
export const setOpenedModelTitle = (title) => {
  threeDStudioStore.setState(state => ({
    ui: {
      ...state.ui,
      openedModel: {
        ...(state.ui?.openedModel || {}),
        title,
      },
    },
  }))
}

/**
 * Shallow-merges a partial settings patch into openedModel.settings.
 * Used after a successful PATCH to /sessions/{id}/settings so the zustand
 * snapshot mirrors the server without a refetch. Keys are camelCase to match
 * the serializer response shape.
 * @param {Object} settingsPatch - Partial settings (e.g. { showGrid: true })
 */
export const setOpenedModelSettingsPatch = (settingsPatch) => {
  if (!settingsPatch || typeof settingsPatch !== 'object' || Array.isArray(settingsPatch)) {
    return
  }

  threeDStudioStore.setState(state => ({
    ui: {
      ...state.ui,
      openedModel: {
        ...(state.ui?.openedModel || {}),
        settings: {
          ...(state.ui?.openedModel?.settings || {}),
          ...settingsPatch,
        },
      },
    },
  }))
}

/**
 * Sets whether a file is being uploaded
 * @param {boolean} isModelUploading
 */
export const setIsModelUploading = (isModelUploading) => {
  setUiState({ isModelUploading })
}

/**
 * Sets whether an image is being uploaded
 * @param {boolean} isImageUploading
 */
export const setIsImageUploading = (isImageUploading) => {
  setUiState({ isImageUploading })
}

export const openUsageModal = () => {
  setUiState({ isUsageModalOpen: true })
}

export const closeUsageModal = () => {
  setUiState({ isUsageModalOpen: false })
}

/**
 * Opens the /ai sign in / sign up modal.
 * @param {'signup' | 'login'} mode - Which screen to open on (default 'signup')
 * @param {'auth' | 'code' | 'success'} step - Which step to open on (default 'auth')
 */
export const openSignInModal = (mode = 'signup', step = 'auth') => {
  setUiState({ isSignInModalOpen: true, signInModalMode: mode, signInModalStep: step })
}

export const closeSignInModal = () => {
  setUiState({ isSignInModalOpen: false })
}

/**
 * Surgically clears the opened model without touching form state.
 *
 * Used by the URL-sync hook when the URL leaves a model detail page
 * (e.g. /ai or /ai/3d-models). Distinct from clearModelSettings, which
 * also resets the user's form draft and is reserved for explicit
 * "Generate New 3D Model" / "+" actions.
 */
export const clearOpenedModel = () => {
  setUiState({ openedModel: {} })
}

/**
 * Toggles whether the Advanced settings group is expanded.
 */
export const toggleAdvancedSettingsExpanded = () => {
  const { isAdvancedSettingsExpanded } = threeDStudioStore.getState().ui
  setUiState({ isAdvancedSettingsExpanded: !isAdvancedSettingsExpanded })
}
